import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Presence,
  PRESENCE_TEXT_TONE,
  PresenceSwatch,
} from '@/components/core/presence-avatar/presence-avatar';
import { APP_PATHS } from '@/constant/app-paths';
import { getOrganizationTimeZone } from '@/lib/utils/custom-date/org-time-zone';
import { cn } from '@/lib/utils';
import { useGetTodayAttendance } from '../../hooks/use-attendance';
import {
  breakTypeOf,
  findOpenBreak,
  formatTimeOfDay,
  isOpenRecord,
  visibleBreaks,
} from '../../services/attendance-rules';

/** A break this long shows as "On break, 2 h+" with a dashed ring (prototype presence rule). */
const LONG_BREAK_MS = 2 * 60 * 60 * 1000;

/** The states your own status takes here (the People list derives the full set). */
type OwnPresence = Extract<Presence, 'in' | 'late' | 'break' | 'breaklong' | 'off'>;

const LABEL: Record<Exclude<OwnPresence, 'off'>, string> = {
  in: 'STATUS_CLOCKED_IN',
  late: 'STATUS_NOT_CLOCKED_IN',
  break: 'STATUS_ON_BREAK',
  breaklong: 'STATUS_ON_BREAK_LONG',
};

/**
 * The signed-in employee's presence today, as the HR Module Prototype shows it: clocked in, on a
 * (lunch) break, on a break for 2 h+, or not clocked in (also after clocking out). Reads the same query
 * as the clock card, so clocking in or taking a break updates it at once. `presence` is `off` while it loads.
 */
export const useAttendancePresence = () => {
  const { t } = useTranslation();
  const { data: attendance, isLoading } = useGetTodayAttendance();

  const openBreak = findOpenBreak(visibleBreaks(attendance?.BreakTimeDetails ?? []));
  const isWorking = Boolean(attendance?.StartTimeUtc) && isOpenRecord(attendance);
  let presence: OwnPresence = 'late';
  if (isLoading) presence = 'off';
  else if (isWorking && openBreak)
    presence =
      Date.now() - new Date(openBreak.StartTimeUtc).getTime() >= LONG_BREAK_MS
        ? 'breaklong'
        : 'break';
  else if (isWorking) presence = 'in';

  // Times show in the zone the day was recorded in, or the app's zone for old records without one.
  const timeZone = attendance?.Timezone ?? getOrganizationTimeZone();
  const since =
    presence === 'in'
      ? attendance?.StartTimeUtc
      : presence === 'break' || presence === 'breaklong'
        ? openBreak?.StartTimeUtc
        : undefined;
  const isLunch = Boolean(openBreak && breakTypeOf(openBreak) === 'Lunch');
  const label =
    presence === 'off'
      ? ''
      : t(presence === 'break' && isLunch ? 'STATUS_ON_LUNCH_BREAK' : LABEL[presence]);
  const detail = since
    ? `${label} · ${t('STATUS_SINCE', { time: formatTimeOfDay(since, false, timeZone) })}`
    : label;

  return { presence, label, detail, isLoading };
};

type AttendanceStatusLinkProps = {
  /** Only the swatch, for the collapsed sidebar rail. The label stays in the tooltip. */
  compact?: boolean;
  onClick?: () => void;
};

/** "Your status" in the sidebar footer: a presence swatch and its label, linking to Attendance. */
export const AttendanceStatusLink = ({ compact = false, onClick }: AttendanceStatusLinkProps) => {
  const { t } = useTranslation();
  const { presence, label, detail, isLoading } = useAttendancePresence();
  const tooltip = isLoading ? t('ATTENDANCE') : t('YOUR_STATUS', { status: detail });

  return (
    <Link
      to={APP_PATHS.ATTENDANCE}
      onClick={onClick}
      aria-label={tooltip}
      title={tooltip}
      data-status={presence}
      className={cn(
        'flex min-w-0 items-center gap-2 rounded-md text-[13px] font-medium hover:bg-sidebar-accent',
        compact ? 'size-8 justify-center' : 'h-8 px-1.5'
      )}
    >
      <PresenceSwatch presence={presence} />
      {!compact && <span className={cn('truncate', PRESENCE_TEXT_TONE[presence])}>{label}</span>}
    </Link>
  );
};
